import React, { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Save, Euro, Loader2 } from "lucide-react";

/**
 * Sezione unificata "Compenso e pagamento" — versione semplificata.
 *
 * Il CRM NON emette fatture: le fatture sono emesse da software esterno.
 * Qui monitoriamo solo la situazione economica della pratica:
 *  - compenso concordato (Fee)
 *  - numero/data/importo fattura esterna (Invoice, inserimento manuale)
 *  - importo incassato (somma Payment)
 *  - saldo residuo (calcolato)
 *  - stato amministrativo (derivato)
 *
 * Riutilizza le tabelle fees/invoices/payments esistenti (nessuna nuova
 * migrazione). Le funzioni avanzate (bozza/emissione automatica/annullamento,
 * numerazione progressiva, report globale) restano nel backend ma non sono
 * esposte in UI.
 */
const STATUS_OPTIONS = [
  { v: "da_fatturare", l: "Da fatturare" },
  { v: "emessa_non_pagata", l: "Fattura emessa – non pagata" },
  { v: "parzialmente_pagata", l: "Parzialmente pagata" },
  { v: "pagata", l: "Pagata" },
];

const money = (v) => (v == null || v === "" ? "0.00" : Number(v).toFixed(2));

function deriveStatus(billed, paid) {
  const b = Number(billed || 0), p = Number(paid || 0);
  if (b <= 0) return "da_fatturare";
  if (p >= b && b > 0) return "pagata";
  if (p > 0) return "parzialmente_pagata";
  return "emessa_non_pagata";
}

export default function PracticeFinance({ practiceId }) {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [feeId, setFeeId] = useState(null);
  const [invoiceId, setInvoiceId] = useState(null);
  const [paymentId, setPaymentId] = useState(null);
  const [form, setForm] = useState({
    agreed_amount: "", status: "da_fatturare",
    invoice_number: "", invoice_date: "", billed_amount: "",
    paid_amount: "", paid_at: "", due_date: "", notes: "",
  });

  const residual = (Number(form.billed_amount || 0) - Number(form.paid_amount || 0)).toFixed(2);

  const reload = useCallback(async () => {
    if (!practiceId) return;
    setLoading(true);
    try {
      const [rf, ri] = await Promise.all([
        api.get(`/practices/${practiceId}/fees`),
        api.get(`/practices/${practiceId}/invoices`),
      ]);
      const fee = (rf.data.items || [])[0] || null;
      const inv = (ri.data.items || [])[0] || null;
      let pay = null;
      let paid_amount = "";
      let paid_at = "";
      if (inv) {
        const rp = await api.get(`/invoices/${inv.id}/payments`);
        pay = (rp.data.items || [])[0] || null;
        paid_amount = inv.paid_amount || "";
        paid_at = pay?.paid_at || "";
      }
      setFeeId(fee?.id || null);
      setInvoiceId(inv?.id || null);
      setPaymentId(pay?.id || null);
      setForm({
        agreed_amount: fee?.amount || "",
        status: inv ? deriveStatus(inv.total_amount, paid_amount) : "da_fatturare",
        invoice_number: inv?.invoice_number || "",
        invoice_date: inv?.invoice_date || "",
        billed_amount: inv?.total_amount || "",
        paid_amount: paid_amount || "",
        paid_at: paid_at || "",
        due_date: inv?.due_date || "",
        notes: inv?.notes || fee?.notes || "",
      });
    } catch (e) { /* toast già mostrato da interceptor */ }
    finally { setLoading(false); }
  }, [practiceId]);

  useEffect(() => { reload(); }, [reload]);

  const save = async () => {
    setBusy(true);
    try {
      // 1) Fee: crea o aggiorna se compenso concordato è valorizzato
      let curFeeId = feeId;
      if (form.agreed_amount) {
        if (!curFeeId) {
          const r = await api.post(`/practices/${practiceId}/fees`, {
            amount: form.agreed_amount, description: null,
            status: "concordato", notes: form.notes || null,
          });
          curFeeId = r.data.id;
        } else {
          await api.patch(`/fees/${curFeeId}`, {
            amount: form.agreed_amount, notes: form.notes || null,
          });
        }
      }

      // 2) Invoice: crea/aggiorna solo se billed_amount > 0 O se status != da_fatturare
      let curInvId = invoiceId;
      const billed = Number(form.billed_amount || 0);
      if (billed > 0 || form.invoice_number) {
        // vat_rate 0 e amount == subtotal == total_amount per corrispondere al valore inserito manualmente
        if (!curInvId) {
          const r = await api.post(`/practices/${practiceId}/invoices`, {
            subtotal: billed || 0, vat_rate: 0, due_date: form.due_date || null,
            fee_id: curFeeId, notes: form.notes || null,
          });
          curInvId = r.data.id;
          // Set numero, data, status manuale via PATCH
          await api.patch(`/invoices/${curInvId}`, {
            invoice_number: form.invoice_number || null,
            invoice_date: form.invoice_date || null,
            status: "emessa",
            subtotal: billed, vat_rate: 0, due_date: form.due_date || null,
          });
        } else {
          await api.patch(`/invoices/${curInvId}`, {
            invoice_number: form.invoice_number || null,
            invoice_date: form.invoice_date || null,
            subtotal: billed, vat_rate: 0,
            due_date: form.due_date || null, notes: form.notes || null,
          });
        }
      }

      // 3) Payment: gestione dell'importo incassato come pagamento singolo
      const paid = Number(form.paid_amount || 0);
      if (curInvId && paid > 0) {
        if (!paymentId) {
          await api.post(`/invoices/${curInvId}/payments`, {
            paid_at: form.paid_at || new Date().toISOString().slice(0, 10),
            amount: paid, payment_method: null, notes: null,
          });
        } else {
          await api.patch(`/payments/${paymentId}`, {
            paid_at: form.paid_at || undefined, amount: paid,
          });
        }
      } else if (paymentId && paid <= 0) {
        // pagamento azzerato -> elimino
        await api.delete(`/payments/${paymentId}`);
      }

      toast.success("Situazione economica salvata");
      await reload();
    } catch (e) { /* interceptor mostra toast */ }
    finally { setBusy(false); }
  };

  if (!practiceId) return null;

  return (
    <div className="ma-section" style={{ marginTop: 24 }} data-testid="practice-finance-section">
      <h2 className="ma-h2"><Euro size={20} style={{ marginRight: 6, verticalAlign: -3 }} />Compenso e pagamento</h2>
      <p className="ma-muted" style={{ fontSize: 13, marginBottom: 12 }}>
        Il CRM non emette fatture: registra i dati della fattura esterna per monitorare la situazione economica della pratica.
      </p>

      {loading ? (
        <div className="ma-loading"><Loader2 className="ma-spin" size={16} /> Caricamento…</div>
      ) : (
        <div className="ma-form-grid">
          <div>
            <label className="ma-label">Compenso concordato (€)</label>
            <input type="number" step="0.01" className="ma-input"
                   value={form.agreed_amount} onChange={e => setForm({ ...form, agreed_amount: e.target.value })}
                   data-testid="fin-agreed"/>
          </div>
          <div>
            <label className="ma-label">Stato amministrativo</label>
            <select className="ma-input" value={form.status}
                    onChange={e => setForm({ ...form, status: e.target.value })}
                    data-testid="fin-status">
              {STATUS_OPTIONS.map(s => <option key={s.v} value={s.v}>{s.l}</option>)}
            </select>
          </div>
          <div>
            <label className="ma-label">Numero fattura esterna</label>
            <input className="ma-input" value={form.invoice_number}
                   onChange={e => setForm({ ...form, invoice_number: e.target.value })}
                   placeholder="inserito manualmente" data-testid="fin-invnum"/>
          </div>
          <div>
            <label className="ma-label">Data fattura</label>
            <input type="date" className="ma-input" value={form.invoice_date}
                   onChange={e => setForm({ ...form, invoice_date: e.target.value })}
                   data-testid="fin-invdate"/>
          </div>
          <div>
            <label className="ma-label">Importo fatturato (€)</label>
            <input type="number" step="0.01" className="ma-input" value={form.billed_amount}
                   onChange={e => setForm({ ...form, billed_amount: e.target.value })}
                   data-testid="fin-billed"/>
          </div>
          <div>
            <label className="ma-label">Importo incassato (€)</label>
            <input type="number" step="0.01" className="ma-input" value={form.paid_amount}
                   onChange={e => setForm({ ...form, paid_amount: e.target.value })}
                   data-testid="fin-paid"/>
          </div>
          <div>
            <label className="ma-label">Saldo residuo (€)</label>
            <input className="ma-input" value={money(residual)} readOnly disabled data-testid="fin-residual"/>
          </div>
          <div>
            <label className="ma-label">Data pagamento (facoltativa)</label>
            <input type="date" className="ma-input" value={form.paid_at}
                   onChange={e => setForm({ ...form, paid_at: e.target.value })}
                   data-testid="fin-paiddate"/>
          </div>
          <div>
            <label className="ma-label">Scadenza pagamento (facoltativa)</label>
            <input type="date" className="ma-input" value={form.due_date}
                   onChange={e => setForm({ ...form, due_date: e.target.value })}
                   data-testid="fin-duedate"/>
          </div>
          <div className="ma-col-full">
            <label className="ma-label">Note amministrative</label>
            <textarea className="ma-input" rows={2} value={form.notes}
                      onChange={e => setForm({ ...form, notes: e.target.value })}
                      data-testid="fin-notes"/>
          </div>
          <div className="ma-col-full">
            <button className="ma-btn-primary" onClick={save} disabled={busy} data-testid="fin-save">
              <Save size={14} /> {busy ? "Salvataggio…" : "Salva situazione economica"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
